import { toZonedTime } from 'date-fns-tz';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { EventCalendarProperties } from '../EventCalendar';
import { useGetDays } from '../hooks';
import { LoaderWithStateAnnouncement } from '../loader-with-state-announcement';
import type { CalendarEvent } from '../types';
import { useGetEventsByDay, useGetMonthViewStyle } from './hooks';
import { MonthDayCell } from './month-day-cell';
import styles from './month-view.module.css';

type MonthViewProperties<T extends CalendarEvent> = Omit<EventCalendarProperties<T>, 'mode'>;

const MonthView = <T extends CalendarEvent>({
  date,
  events,
  isLoading,
  onMoreEventsClick,
  renderEvent,
  renderTimeSlot,
  timezones,
  weekStartsOn,
}: MonthViewProperties<T>) => {
  const { t } = useTranslation(['translationReactComponents']);
  const primaryTimezone = timezones[0];
  const days = useGetDays({ date, mode: 'month', timezone: primaryTimezone, weekStartsOn });
  const { gridStyle, monthViewStyle } = useGetMonthViewStyle({ days });
  const displayMonth = useMemo(() => toZonedTime(date, primaryTimezone), [date, primaryTimezone]);
  const eventsByDay = useGetEventsByDay({ days, events, primaryTimezone });

  return (
    <div className={styles['month-view']} style={monthViewStyle}>
      <LoaderWithStateAnnouncement isLoading={isLoading} />
      <div className={styles['weekday-header']}>
        {days.slice(0, 7).map((day) => (
          <div className={styles.weekday} key={`weekday-${day.toISOString()}`}>
            {t('dateFormats.dayShort', { date: day })}
          </div>
        ))}
      </div>
      <div className={styles.grid} style={gridStyle}>
        {days.map((day) => (
          <MonthDayCell<T>
            day={day}
            displayMonth={displayMonth}
            events={eventsByDay.get(day.toISOString()) ?? []}
            key={day.toISOString()}
            onMoreEventsClick={onMoreEventsClick}
            renderEvent={renderEvent}
            renderTimeSlot={renderTimeSlot}
            timezone={primaryTimezone}
          />
        ))}
      </div>
    </div>
  );
};

export default MonthView;
